import { useState } from "react";
import { twMerge } from "tailwind-merge";
import type { SxProps, Theme } from "@mui/material/styles";

import Icons from "assets";
import { Chip } from "ui/chip";
import { Box } from "ui/box";
import { Button } from "ui/button";
import { Collapse } from "ui/collapse";
import { Switch } from "ui/switch";

import FormBuilderFieldDisplay from "containers/admin/clients/client/components/form-builder/form-builder-field-display";
import { renderSectionHeading } from "containers/admin/clients/client/components/form-builder/form-builder-section-heading";
import {
  buildDynamicSectionItemCopyText,
  formatFieldDisplayValueForReadOnly,
  getNestedObjectDisplayLines,
  hasFieldDisplayContent,
  isDynamicSectionField,
  isEmptyDisplayValue,
} from "containers/admin/clients/client/components/form-builder/form-builder-viewer-utils";
import { isObjectSchema, isRecord } from "containers/admin/clients/client/components/form-builder/form-builder-utils";
import {
  getDynamicSectionConfig,
  getDynamicSectionHeaderFieldNames,
  getFieldTitle,
  isFieldHiddenInEdit,
  resolveDynamicSectionItemLabel,
  sortJsonFormFields,
  type JsonFormObjectSchema,
  type JsonFormSchema,
  type JsonFormSectionHeadingLevel,
} from "containers/admin/clients/client/components/form-builder/form-schema";

type DynamicSectionRow = Record<string, unknown>;

const COPY_TEXT_BUTTON_SX: SxProps<Theme> = { flexShrink: 0 };

const SECTION_CONTAINER_SX: SxProps<Theme> = {
  display: "flex",
  width: "100%",
  flexDirection: "column",
  gap: 0.75,
};

type FormBuilderViewerDynamicSectionProps = {
  title: string;
  headingLevel?: JsonFormSectionHeadingLevel;
  field: JsonFormSchema;
  itemSchema: JsonFormObjectSchema;
  rows: DynamicSectionRow[];
};

const renderScalarOrObjectField = (name: string, field: JsonFormSchema, value: unknown) => {
  if (isEmptyDisplayValue(value)) {
    return null;
  }

  if (isObjectSchema(field) && isRecord(value)) {
    const nestedLines = getNestedObjectDisplayLines(field, value);

    return nestedLines.map(({ label, value: displayValue }) => (
      <FormBuilderFieldDisplay key={`${name}-${label}`} label={label} value={displayValue} />
    ));
  }

  return (
    <FormBuilderFieldDisplay
      key={name}
      label={getFieldTitle(name, field)}
      value={formatFieldDisplayValueForReadOnly(field, value)}
    />
  );
};

const FormBuilderViewerDynamicSection = ({
  title,
  headingLevel = "subsection",
  field,
  itemSchema,
  rows,
}: FormBuilderViewerDynamicSectionProps) => {
  const config = getDynamicSectionConfig(field);
  const isStacked = config.readOnlyLayout === "stacked";
  const hasExplicitReadOnlyLayout = config.readOnlyLayout !== "auto";
  const isCollapsible = config.collapsible && !isStacked;
  const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>({});

  if (rows.length === 0) {
    if (!config.emptyMessage) {
      return null;
    }

    return (
      <div className="flex w-full max-w-[600px] flex-col gap-1.5">
        {title.trim() && renderSectionHeading(title, headingLevel)}
        <p className="text-base font-semibold leading-6 text-gray-600">{config.emptyMessage}</p>
      </div>
    );
  }

  const headerFieldNames = getDynamicSectionHeaderFieldNames(field);
  const showItemLabels = !(config.hideSingleItemLabel && rows.length === 1);

  const handleCopyText = (row: DynamicSectionRow) => {
    const text = buildDynamicSectionItemCopyText(row, itemSchema);

    if (text) {
      void navigator.clipboard.writeText(text);
    }
  };

  const toggleCollapsed = (index: number) => {
    setCollapsedItems((previous) => {
      const currentlyCollapsed = previous[index] ?? config.defaultCollapsed;

      return {
        ...previous,
        [index]: !currentlyCollapsed,
      };
    });
  };

  const renderItemFields = (row: DynamicSectionRow) =>
    sortJsonFormFields(Object.entries(itemSchema.properties)).flatMap(([name, itemField]) => {
      if (headerFieldNames.has(name) || isFieldHiddenInEdit(itemField)) {
        return [];
      }

      const value = row[name];

      if (!hasFieldDisplayContent(itemField, value)) {
        return [];
      }

      if (isDynamicSectionField(itemField) && itemField.items?.properties && Array.isArray(value)) {
        const nestedRows = value.filter((item): item is DynamicSectionRow => isRecord(item));

        return [
          <FormBuilderViewerDynamicSection
            key={name}
            title={getFieldTitle(name, itemField)}
            headingLevel={itemField["x-heading-level"] ?? "subsection"}
            field={itemField}
            itemSchema={itemField.items as JsonFormObjectSchema}
            rows={nestedRows}
          />,
        ];
      }

      const fieldNode = renderScalarOrObjectField(name, itemField, value);

      return fieldNode ? [fieldNode] : [];
    });

  // An explicit `readOnlyLayout` (cards/stacked) wins over `chromeless` here, in the read-only
  // viewer only — edit mode (`FormBuilderDynamicSection`) keeps honoring `chromeless` unconditionally.
  if (config.chromeless && !hasExplicitReadOnlyLayout) {
    return (
      <Box sx={SECTION_CONTAINER_SX}>
        {title.trim() && renderSectionHeading(title, headingLevel)}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {rows.map((row, index) => (
            <Box
              key={`dynamic-section-row-${index}`}
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              data-testid={`dynamic-section-item-${index}`}
            >
              {renderItemFields(row)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const renderHeaderToggle = (row: DynamicSectionRow) => {
    if (!config.headerToggleField) {
      return null;
    }

    const toggleField = itemSchema.properties[config.headerToggleField];

    if (!toggleField) {
      return null;
    }

    return (
      <Switch
        toggleName={getFieldTitle(config.headerToggleField, toggleField)}
        checked={Boolean(row[config.headerToggleField])}
        disabled
        labelPlacement="start"
        sx={{ flexShrink: 0 }}
      />
    );
  };

  const renderItemBadge = (row: DynamicSectionRow) => {
    if (!config.badgeField || !row[config.badgeField]) {
      return null;
    }

    return (
      <Chip
        size="small"
        color="primary"
        variant="filled"
        label={config.badgeLabel ?? "Default"}
      />
    );
  };

  return (
    <Box sx={SECTION_CONTAINER_SX}>
      {title.trim() && renderSectionHeading(title, headingLevel)}
      <div className={twMerge("flex flex-col", isStacked ? "gap-6" : "gap-3")}>
        {rows.map((row, index) => {
          const isCollapsed = isCollapsible
            ? (collapsedItems[index] ?? config.defaultCollapsed)
            : false;
          const itemLabel = resolveDynamicSectionItemLabel(row, index, field);
          const fieldNodes = renderItemFields(row);
          const subtitleValue = config.itemSubtitleField
            ? row[config.itemSubtitleField]
            : undefined;
          const subtitle =
            config.itemSubtitleField &&
            subtitleValue !== undefined &&
            subtitleValue !== null &&
            String(subtitleValue).trim()
              ? `${config.itemSubtitlePrefix ?? ""}${String(subtitleValue)}`
              : null;
          const headerToggle = renderHeaderToggle(row);
          const itemBadge = renderItemBadge(row);
          const showTitleRow = showItemLabels || Boolean(headerToggle) || Boolean(subtitle);

          if (isStacked) {
            return (
              <div
                key={`dynamic-section-row-${index}`}
                className="flex flex-col gap-2"
                data-testid={`dynamic-section-stacked-item-${index}`}
              >
                {showItemLabels && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold leading-5 text-navy-800">{itemLabel}</span>
                    {itemBadge}
                  </div>
                )}
                {fieldNodes.length > 0 && (
                  <div className="flex flex-col gap-3">{fieldNodes}</div>
                )}
              </div>
            );
          }

          return (
            <div
              key={[
                row.channel,
                row.audienceType,
                row.quantity,
                row.creativeDetails,
                row.cadence,
                row.emailTypeId,
                row.emailTypeName,
                index,
              ]
                .filter((part) => part !== undefined && part !== "")
                .join("-") || `dynamic-section-row-${index}`}
              className="flex flex-col gap-3 rounded-md border border-navy-200 px-3 py-2.5"
              data-testid={`dynamic-section-card-${index}`}
            >
              {showTitleRow && (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    {showItemLabels ? (
                      isCollapsible ? (
                        <button
                          type="button"
                          className="flex min-w-0 items-center gap-1"
                          aria-expanded={!isCollapsed}
                          onClick={() => toggleCollapsed(index)}
                        >
                          <span className="truncate text-base font-bold leading-6 text-gray-900">
                            {itemLabel}
                          </span>
                          <Icons.ChevronDown
                            aria-hidden
                            className={twMerge(
                              "size-5 shrink-0 text-neutrals-500 transition-transform",
                              isCollapsed && "-rotate-90",
                            )}
                          />
                        </button>
                      ) : (
                        <span className="truncate text-base font-bold leading-6 text-gray-900">
                          {itemLabel}
                        </span>
                      )
                    ) : null}
                    {subtitle && (
                      <p className="text-sm font-semibold leading-5 tracking-[0.25px] text-gray-500">
                        {subtitle}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {headerToggle}
                    {config.showCopyText && (
                      <Button
                        variant="text"
                        sx={COPY_TEXT_BUTTON_SX}
                        aria-label={`Copy text for ${itemLabel}`}
                        startIcon={<Icons.ContentCopy aria-hidden />}
                        onClick={() => handleCopyText(row)}
                      >
                        Copy text
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {/* `unmountOnExit`: a collapsed `Collapse` is still a flex item, so the card's own gap
                  would leave a phantom row under the header. */}
              {fieldNodes.length > 0 && (
                <Collapse in={!isCollapsed} unmountOnExit>
                  <div className="flex flex-col gap-3">{fieldNodes}</div>
                </Collapse>
              )}
            </div>
          );
        })}
      </div>
    </Box>
  );
};

export default FormBuilderViewerDynamicSection;
